import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import ComprobanteTicket from './ComprobanteTicket';

const N8N_DEUNA            = 'https://nuevo-n8n.u5r75b.easypanel.host/webhook/sisnetel/pichincha/deuna';
const N8N_PAYPHONE_ESTADO  = process.env.REACT_APP_N8N_PAYPHONE_ESTADO!;
const N8N_COMPROBANTE      = process.env.REACT_APP_N8N_URL_COMPROBANTE!;
const N8N_DETALLE_FACTURA  = process.env.REACT_APP_N8N_URL_DETALLE_FACTURA!;

const TIMEOUT_SEG = 180;
const POLL_SEG    = 10;

interface DeunaClienteModalProps {
  show: boolean;
  onHide: () => void;
  monto: number;
  cedula: string | number;
  nombre: string;
  estado?: string;
  facturaId?: string | number;
}

type Etapa = 'inicio' | 'qr' | 'pagado' | 'timeout' | 'comprobante';

const DeunaClienteModal: React.FC<DeunaClienteModalProps> = ({
  show, onHide, monto, cedula, nombre, estado, facturaId,
}) => {
  const [etapa,    setEtapa]    = useState<Etapa>('inicio');
  const [qrData,   setQrData]   = useState<string | null>(null);
  const [codigo,   setCodigo]   = useState<string | null>(null);
  const [error,    setError]    = useState<string | null>(null);
  const [loading,  setLoading]  = useState(false);
  const [segundos, setSegundos] = useState(TIMEOUT_SEG);
  const [svgBase64,setSvgBase64]= useState('');

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>  | null>(null);
  const resolvedRef= useRef(false);

  const estadoUp    = (estado || '').toUpperCase();
  const estadoColor = estadoUp === 'ACTIVO' ? 'var(--sn-exito-texto)' : 'var(--sn-peligro-texto)';
  const estadoBg    = estadoUp === 'ACTIVO' ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)';
  const estadoEmoji = estadoUp === 'ACTIVO' ? '●' : '●';

  const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const pct     = Math.round(((TIMEOUT_SEG - segundos) / TIMEOUT_SEG) * 100);

  const detener = () => {
    if (pollingRef.current)  { clearInterval(pollingRef.current);  pollingRef.current  = null; }
    if (countRef.current)    { clearInterval(countRef.current);    countRef.current    = null; }
    if (timeoutRef.current)  { clearTimeout(timeoutRef.current);   timeoutRef.current  = null; }
  };

  const handleCerrar = () => {
    detener();
    onHide();
    window.location.reload();
  };

  useEffect(() => {
    if (!show) {
      detener();
      setEtapa('inicio'); setQrData(null); setCodigo(null);
      setError(null); setSegundos(TIMEOUT_SEG); setSvgBase64('');
      resolvedRef.current = false;
    }
  }, [show]);

  const generarComprobante = async () => {
    try {
      const clientData = (() => { try { return JSON.parse(localStorage.getItem('clientData') || '{}'); } catch { return {}; } })();
      const now = new Date();
      const p2  = (n: number) => String(n).padStart(2, '0');
      const fechaImpresion = `${p2(now.getDate())}/${p2(now.getMonth()+1)}/${now.getFullYear()} ${p2(now.getHours())}:${p2(now.getMinutes())}:${p2(now.getSeconds())}`;
      const fechaHoy = `${p2(now.getDate())}/${p2(now.getMonth()+1)}/${now.getFullYear()}`;
      const fmtDate  = (d: string) => { if (!d || d.startsWith('0000')) return ''; const pts = d.split('-'); return pts.length === 3 ? `${pts[2]}/${pts[1]}/${pts[0]}` : d; };

      let planNombre='', facturacion='', suspension='', vencimiento='';
      let subtotal = monto.toFixed(2), iva='0.00', total=monto.toFixed(2), fechaPago=fechaHoy;

      try {
        const resD  = await fetch(N8N_DETALLE_FACTURA, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ facturas_id: facturaId }),
        });
        const rawD   = await resD.json();
        const detalle = Array.isArray(rawD) ? rawD[0] : rawD;
        if (detalle?.estado === 'exito') {
          const fac   = detalle.factura || {};
          const items: any[] = detalle.items || [];
          const item  = items.find((i: any) => parseFloat(i.total) > 0) || items[0];
          const lines: string[] = (item?.descrp || '').split('\n').map((l: string) => l.trim()).filter(Boolean);
          planNombre  = lines[0] || '';
          facturacion = (lines[2] || '').replace('facturación del ', '');
          suspension  = (lines[3] || '').replace('Fecha de Suspensión : ', '');
          vencimiento = fmtDate(fac.vencimiento || '');
          fechaPago   = fmtDate(fac.fechapago || '') || fechaHoy;
          subtotal    = parseFloat(fac.subtotal  || monto.toString()).toFixed(2);
          iva         = parseFloat(fac.impuesto  || '0').toFixed(2);
          total       = parseFloat(fac.total     || monto.toString()).toFixed(2);
        }
      } catch {}

      const payload = {
        clienteNombre: nombre, clienteCedula: String(cedula),
        clienteCelular: clientData?.movil || '', clienteCorreo: clientData?.correo || '',
        facturaId: String(facturaId || '').padStart(8, '0'),
        planNombre, facturacion, suspension, fechaPago, vencimiento,
        subtotal, iva, total, saldo: '0.00', pasarela: 'DeUna / Banco Pichincha',
        numeroComprobante: '', autorizacion: '',
        montoDepositado: monto.toFixed(2), fechaImpresion,
      };

      const res  = await fetch(N8N_COMPROBANTE, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data?.base64) setSvgBase64(data.base64);
    } catch {}
  };

  const procesarPagado = async () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    detener();
    setEtapa('pagado');
    await generarComprobante();
  };

  const consultarEstado = async (): Promise<boolean> => {
    try {
      const res  = await fetch(N8N_PAYPHONE_ESTADO, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ facturaId }),
      });
      const data = await res.json();
      const raw  = Array.isArray(data) ? data[0] : data;
      if (raw?.estado?.toLowerCase() === 'pagado') {
        await procesarPagado();
        return true;
      }
    } catch {}
    return false;
  };

  const iniciarPolling = () => {
    resolvedRef.current = false;
    setSegundos(TIMEOUT_SEG);

    countRef.current = setInterval(() => {
      setSegundos(s => {
        if (s <= 1) { clearInterval(countRef.current!); return 0; }
        return s - 1;
      });
    }, 1000);

    pollingRef.current = setInterval(async () => {
      if (resolvedRef.current) { detener(); return; }
      await consultarEstado();
    }, POLL_SEG * 1000);

    timeoutRef.current = setTimeout(() => {
      if (!resolvedRef.current) {
        resolvedRef.current = true;
        detener();
        setEtapa('timeout');
      }
    }, TIMEOUT_SEG * 1000);
  };

  const handleGenerarQR = async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(N8N_DEUNA, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula: String(cedula), nombre, monto, facturaId: facturaId || '' }),
      });
      const data = await res.json();
      const raw  = Array.isArray(data) ? data[0] : data;
      if (raw?.status === '1' || raw?.qr) {
        setQrData(raw.qr);
        setCodigo(raw.numericCode || null);
        setEtapa('qr');
        iniciarPolling();
      } else {
        setError('No se pudo generar el QR. Intenta más tarde.');
      }
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // ── Tarjeta cliente (reutilizada en todas las etapas) ────────────
  const TarjetaCliente = () => (
    <div style={{ margin: '0 0 10px', background: 'var(--sn-superficie-2)', borderRadius: 8, padding: '8px 12px', border: '1px solid var(--sn-borde)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--sn-texto)' }}>{nombre}</span>
        {estadoUp && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: estadoBg, color: estadoColor, whiteSpace: 'nowrap', marginLeft: 8 }}>
            {estadoEmoji} {estadoUp}
          </span>
        )}
      </div>
      <div style={{ borderTop: '1px solid var(--sn-borde)', paddingTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 9, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Cédula</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--sn-texto-2)' }}>{cedula}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Factura</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--sn-texto-2)' }}>#{facturaId}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 9, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Monto</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--sn-alerta-texto)', lineHeight: 1.2 }}>${monto.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );

  // ── Comprobante ──────────────────────────────────────────────────
  if (etapa === 'comprobante' && svgBase64) {
    return (
      <Modal show onHide={handleCerrar} centered>
        <Modal.Header closeButton style={{ borderBottom: 'none' }}>
          <Modal.Title style={{ fontSize: 15, fontWeight: 700 }}>🧾 Comprobante de Pago</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ComprobanteTicket svgBase64={svgBase64} facturaId={String(facturaId || '')} onClose={handleCerrar} />
        </Modal.Body>
      </Modal>
    );
  }

  // ── Pagado ───────────────────────────────────────────────────────
  if (etapa === 'pagado') {
    return (
      <Modal show onHide={handleCerrar} centered>
        <Modal.Body className="text-center py-4 px-3">
          <div style={{ fontSize: 72 }}>🎉</div>
          <h4 className="mt-2 fw-bold text-success">¡Pago exitoso!</h4>
          <p className="text-muted mt-2 mb-1">El pago de <strong>${monto.toFixed(2)}</strong> fue procesado correctamente.</p>
          <p className="text-muted mb-3">Factura <strong>#{facturaId}</strong> — <strong>{nombre}</strong></p>
          <div className="d-flex gap-2 mt-3">
            {svgBase64 && (
              <Button variant="success" className="w-100" onClick={() => setEtapa('comprobante')}>
                🧾 Ver comprobante
              </Button>
            )}
            <Button variant="outline-secondary" className="w-100" onClick={handleCerrar}>Cerrar</Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // ── Timeout ──────────────────────────────────────────────────────
  if (etapa === 'timeout') {
    const verificarManual = async () => {
      resolvedRef.current = false;
      const pagado = await consultarEstado();
      if (!pagado) resolvedRef.current = true;
    };
    return (
      <Modal show onHide={handleCerrar} centered>
        <Modal.Body className="text-center py-4 px-3">
          <div style={{ fontSize: 56 }}>⏰</div>
          <h4 className="mt-2 fw-bold text-secondary">Tiempo agotado</h4>
          <p className="text-muted mt-2 mb-3">
            No se detectó el pago en 3 minutos.<br />
            Si ya pagaste, puedes verificarlo manualmente.
          </p>
          <Button variant="primary" className="w-100 mb-2" onClick={verificarManual}>
            🔄 Comprobar mi pago
          </Button>
          <Button variant="outline-secondary" className="w-100" onClick={handleCerrar}>Cerrar</Button>
        </Modal.Body>
      </Modal>
    );
  }

  // ── QR activo ────────────────────────────────────────────────────
  if (etapa === 'qr' && qrData) {
    return (
      <Modal show onHide={handleCerrar} centered>
        <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>🏦 Pago con Banco Pichincha</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ paddingTop: 10 }}>
          <TarjetaCliente />
          <div className="text-center">
            <img src={qrData} alt="QR DeUna" style={{ width: '65%', maxWidth: 220, borderRadius: 12, border: '2px solid var(--sn-borde)' }} />
            {codigo && (
              <div style={{ marginTop: 8, background: 'var(--sn-info-suave)', borderRadius: 8, padding: '5px 14px', display: 'inline-block' }}>
                <div style={{ fontSize: 10, color: 'var(--sn-info-texto)', textTransform: 'uppercase', letterSpacing: 0.4 }}>Código numérico</div>
                <div style={{ fontWeight: 600, fontSize: 22, letterSpacing: 5, color: 'var(--sn-texto)' }}>{codigo}</div>
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <p style={{ fontSize: 12, color: 'var(--sn-texto-3)', marginBottom: 4 }}>
                QR válido por <strong style={{ color: segundos < 30 ? 'var(--sn-peligro)' : 'var(--sn-texto)' }}>{fmtTime(segundos)}</strong>
                {' · '}
                <span style={{ fontSize: 11 }}>verificando cada {POLL_SEG}s</span>
              </p>
              <div style={{ background: 'var(--sn-superficie-3)', borderRadius: 8, height: 4, margin: '0 4px' }}>
                <div style={{ background: segundos < 30 ? 'var(--sn-peligro)' : 'var(--sn-marca)', borderRadius: 8, height: 4, width: `${100 - pct}%`, transition: 'width 1s linear' }} />
              </div>
            </div>
            <Button variant="outline-primary" size="sm" className="mt-2 w-100" onClick={() => consultarEstado()}>
              🔄 Verificar ahora
            </Button>
            <div style={{ marginTop: 8, background: 'var(--sn-exito-suave)', borderRadius: 8, padding: '7px 10px', textAlign: 'left' }}>
              <p style={{ fontSize: 11, color: 'var(--sn-exito-texto)', margin: 0 }}>
                <strong>¿Cómo pagar?</strong> Abre Banco Pichincha → DeUna → escanea el QR o ingresa el código numérico.
              </p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: 'none', paddingTop: 0 }}>
          <Button variant="outline-secondary" onClick={handleCerrar}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // ── Modal inicial ────────────────────────────────────────────────
  if (!show) return null;
  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>🏦 Pago con Banco Pichincha</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ paddingTop: 10 }}>
        <TarjetaCliente />
        {error && <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div>}
        <p style={{ fontSize: 12, color: 'var(--sn-texto-3)', margin: 0 }}>🔒 Pago seguro a través de DeUna / Banco Pichincha</p>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: 'none', paddingTop: 0 }}>
        <Button variant="outline-secondary" onClick={onHide} disabled={loading}>Cancelar</Button>
        <Button variant="primary" onClick={handleGenerarQR} disabled={loading}>
          {loading ? <><Spinner animation="border" size="sm" className="me-2" />Generando...</> : '📱 Generar QR'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeunaClienteModal;
